import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Upload, Plus, Download, Trash2, Loader2, Lock, ShieldCheck } from "lucide-react";
import { ROOM_DOC_TYPES, ROOM_DOC_STATUSES, DOC_AUDIENCES, docTypeLabel, docStatusMeta } from "./meetingConstants";

export default function MeetingDocuments({ meetingId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqOpen, setReqOpen] = useState(false);
  const [req, setReq] = useState({ name: "", doc_type: "home_photos", visibility: ["client"], notes: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/meetings/${meetingId}/documents`); setDocs(data.documents || []); }
    catch (e) { toast.error(errMsg(e, "Could not load documents.")); } finally { setLoading(false); }
  }, [meetingId]);
  useEffect(() => { load(); }, [load]);

  const doRequest = async () => {
    if (!req.name.trim()) return toast.error("Give the document a name.");
    setBusy(true);
    try { await api.post(`/meetings/${meetingId}/documents/request`, req); toast.success("Document requested."); setReqOpen(false); setReq({ name: "", doc_type: "home_photos", visibility: ["client"], notes: "" }); load(); }
    catch (e) { toast.error(errMsg(e, "Request failed.")); } finally { setBusy(false); }
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("name", file.name); fd.append("doc_type", "other"); fd.append("visibility", "");
      await api.post(`/meetings/${meetingId}/documents/upload`, fd); toast.success("Uploaded (internal only). Set who can see it below."); load();
    } catch (er) { toast.error(errMsg(er, "Upload failed.")); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const fulfill = async (docId, file) => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("doc_id", docId); await api.post(`/meetings/${meetingId}/documents/upload`, fd); toast.success("File uploaded."); load(); }
    catch (er) { toast.error(errMsg(er, "Upload failed.")); } finally { setBusy(false); }
  };
  const update = async (docId, patch) => {
    try { await api.put(`/room-documents/${docId}`, patch); load(); } catch (e) { toast.error(errMsg(e, "Update failed.")); }
  };
  const download = async (d) => {
    try { const res = await api.get(`/room-documents/${d.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(res.data); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Could not open file."); }
  };
  const remove = async (docId) => { try { await api.delete(`/room-documents/${docId}`); toast.success("Archived."); load(); } catch { toast.error("Failed."); } };
  const toggleAud = (docId, current, aud) => {
    const next = current.includes(aud) ? current.filter((x) => x !== aud) : [...current, aud];
    update(docId, { visibility: next });
  };

  return (
    <Card className="p-4 border-[#E6EDF2]" data-testid="meeting-documents-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#1C4358]/70"><FileText size={14} /> Documents</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" data-testid="doc-request-btn" onClick={() => setReqOpen(true)} className="h-7 border-[#D6E0E7] text-[#1C4358] gap-1 text-[12px]"><Plus size={13} /> Request</Button>
          <label className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-[#1C4358] hover:bg-[#163545] text-white text-[12px] font-medium cursor-pointer" data-testid="doc-upload-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
            <input ref={fileRef} type="file" className="hidden" onChange={onUpload} />
          </label>
        </div>
      </div>
      <p className="flex items-start gap-1.5 text-[11px] text-[#4B5563] mb-3"><ShieldCheck size={12} className="text-[#2F7D5A] mt-0.5" /> New uploads are internal-only until you tick who can see them. Document sharing & review coordination only — not legal advice.</p>
      {loading ? <div className="py-4 text-center"><Loader2 className="animate-spin text-[#1C4358] inline" size={18} /></div> :
        docs.length === 0 ? <div className="text-[13px] text-[#9AA5B1]">No documents yet. Request one from the family or upload a file.</div> : (
          <div className="space-y-2">
            {docs.map((d) => {
              const sm = docStatusMeta(d.status);
              const vis = d.visibility || [];
              return (
                <div key={d.id} className="rounded-lg border border-[#E6EDF2] p-2.5" data-testid={`doc-row-${d.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium text-[#1C4358] truncate">{d.name}</div>
                      <div className="text-[11px] text-[#4B5563]">{docTypeLabel(d.doc_type)}{d.uploaded_by_name ? ` · by ${d.uploaded_by_name}` : ""}{vis.length === 0 ? " · 🔒 internal only" : ""}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {d.uploaded && <button onClick={() => download(d)} className="p-1.5 rounded hover:bg-[#F1F5F8] text-[#1C4358]" data-testid={`doc-download-${d.id}`}><Download size={14} /></button>}
                      {!d.uploaded && <label className="p-1.5 rounded hover:bg-[#F1F5F8] text-[#2F7D5A] cursor-pointer" title="Upload file for this request"><Upload size={14} /><input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && fulfill(d.id, e.target.files[0])} /></label>}
                      <button onClick={() => remove(d.id)} className="p-1.5 rounded hover:bg-[#F1F5F8] text-[#9C1E22]"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Select value={d.status} onValueChange={(v) => update(d.id, { status: v })}>
                      <SelectTrigger className="h-7 w-[150px] text-[12px]" style={{ color: sm.hex }}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROOM_DOC_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-1">
                      {DOC_AUDIENCES.map((a) => (
                        <button key={a.key} onClick={() => toggleAud(d.id, vis, a.key)} data-testid={`doc-vis-${d.id}-${a.key}`}
                          className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${vis.includes(a.key) ? "bg-[#2F7D5A] border-[#2F7D5A] text-white" : "border-[#D6E0E7] text-[#4B5563]"}`}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="sm:max-w-md" data-testid="doc-request-dialog">
          <DialogHeader><DialogTitle className="text-[#1C4358]">Request a document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px] text-[#4B5563]">What do you need?</Label><Input data-testid="doc-request-name" value={req.name} onChange={(e) => setReq({ ...req, name: e.target.value })} placeholder="e.g. Home photos" className="h-10" /></div>
            <div><Label className="text-[12px] text-[#4B5563]">Type</Label>
              <Select value={req.doc_type} onValueChange={(v) => setReq({ ...req, doc_type: v })}>
                <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{ROOM_DOC_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px] text-[#4B5563]">Who can see / upload it</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {DOC_AUDIENCES.map((a) => (
                  <button key={a.key} type="button" onClick={() => setReq({ ...req, visibility: req.visibility.includes(a.key) ? req.visibility.filter((x) => x !== a.key) : [...req.visibility, a.key] })}
                    className={`text-[12px] px-2.5 py-1 rounded-full border ${req.visibility.includes(a.key) ? "bg-[#2F7D5A] border-[#2F7D5A] text-white" : "border-[#D6E0E7] text-[#4B5563]"}`}>{a.label}</button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button>
            <Button data-testid="doc-request-submit" onClick={doRequest} disabled={busy} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white">Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
